import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck.js';
import { addPendingFavorite, removePendingFavorite } from '../../ducks/pendingActions.duck';

import {
  H3,
  H5,
  IconSearch,
  LayoutSingleColumn,
  NamedRedirect,
  Page,
  ReviewRating,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
} from './SearchPage.shared';
import { handleToggleFavorites } from '../ListingPage/ListingPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import Icons from '../../components/Icons/Icons.js';
import SectionShopByCategory from './SectionShopByCategory.js';
import SectionStillOnHunt from './SectionStillOnHunt.js';
import SectionAdBanner from './SectionAdBanner.js';
import SectionAdBannerSecondary from './SectionAdBannerSecondary.js';
import SectionConclusion from './SectionConclusion.js';
import SectionCollections from './SectionCollections.js';
import { CATEGORY_LEVEL } from '../../util/enums.js';
import { isArrayLength } from '../../util/genericHelper.js';
import { recentSearches } from '../../util/constants.js';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.saveRecentSearch();
    }
  }

  saveRecentSearch() {
    const { location } = this.props;
    const search = location.search;
    if (!search) return;

    const params = parse(search);
    const label = this.generateSearchLabel(params);
    const url = location.pathname + search;

    let recent = JSON.parse(localStorage.getItem(recentSearches)) || [];
    recent = recent.filter(r => r.url !== url);
    recent.unshift({ label, url });
    if (recent.length > 50) recent.pop();
    localStorage.setItem(recentSearches, JSON.stringify(recent));
  }

  generateSearchLabel(params) {
    const { config } = this.props;
    const { listingCategories } = config.categoryConfiguration || {};
    let labels = [];

    const categoryParams = config?.search?.defaultFilters?.nestedParams || CATEGORY_LEVEL;
    categoryParams.forEach(param => {
      if (params[param]) {
        labels.push(this.getCategoryLabel(params[param], listingCategories));
      }
    });

    if (params.keywords) {
      labels.push(params.keywords);
    }

    if (params.price) {
      const [min, max] = params.price.split(',').map(p => `$${p}`);
      labels.push(`Price: ${min}${max ? `-${max}` : ''}`);
    }

    return labels.join(' ') || 'other';
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  removeFilter(paramName) {
    const handleChange = this.getHandleChangedValueFn(true);
    handleChange({ [paramName]: undefined });
  }

  getCategoryLabel(key, listingCategories) {
    if (!key) return null;
    if (!isArrayLength(listingCategories)) {
      return key;
    }
    for (const cat of listingCategories) {
      if (cat.key === key) return cat.label;
      if (cat.subcategories) {
        for (const sub of cat.subcategories) {
          if (sub.key === key) return sub.label;
        }
      }
    }
    return key;
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      currentUser,
      onUpdateFavorites,
      onAddPendingFavorite,
      onRemovePendingFavorite,
      pendingFavorites,
      history,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    // Create favorites toggle handler
    const createToggleFavoritesHandler = listingId => isFavorite => {
      const commonParams = {
        currentUser,
        routes: routeConfiguration,
        location,
        history: this.props.history,
        onUpdateFavorites,
        onAddPendingFavorite,
        onRemovePendingFavorite,
      };

      const params = { id: listingId };
      const toggleHandler = handleToggleFavorites({
        ...commonParams,
        params,
      });

      toggleHandler(isFavorite);
    };

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;
    const useHistoryPush = (page, params = {}, search = {}) => {
      history.push(createResourceLocatorString(page, routeConfiguration, params, search));
    };

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    const categoryParams = defaultFiltersConfig?.nestedParams || CATEGORY_LEVEL;
    const selectedCategoryFilters = categoryParams
      .map(param => ({
        param,
        key: selectedFilters[param],
      }))
      .filter(c => c.key);

    // const currentKeywords = searchParams.get('keywords') || '';
    const handleSearchSubmit = e => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const keywords = formData.get('keywords');
      const params = new URLSearchParams(location.search);
      if (keywords) {
        params.set('keywords', keywords);
      } else {
        params.delete('keywords');
      }
      history.push(`${location.pathname}?${params.toString()}`);
    };
    const search = (
      <div className={css.searchContainer}>
        <form onSubmit={handleSearchSubmit} className={css.searchForm}>
          <span className={css.searchIcon}>
            <IconSearch />
          </span>
          <input
            name="keywords"
            type="text"
            className={css.searchInput}
            placeholder={intl.formatMessage({ id: 'SearchResultsPanel.searchPlaceholder' })}
            // defaultValue={currentKeywords}
          />
        </form>
      </div>
    );
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          className={css.pageRoot}
          topbar={<TopbarContainer currentSearchParams={validQueryParams} />}
          footer={<FooterContainer />}
        >
          <div className={css.promoBanner}>
            {/* <div className={css.fixedWidthContainer}>
              <div className={css.joinCta}>
                Are you an interior designer? {''}
                <a href="/">Join our trade program</a> {''}
                to enjoy exclusive benefits!
              </div>
              <div className={css.designerCta}>
                Discover Designers: View the {''}
                <a href="/"> {''}Designer Directory</a>
              </div>
            </div> */}
          </div>
          <div className={css.layoutWrapperContainer}>
            <div className={css.container}>
              <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
                <div className={css.filterColumnContent}>
                  <div className={css.searchResultSummary}>
                    {searchInProgress ? (
                      <FormattedMessage id="MainPanelHeader.loadingResults" />
                    ) : (
                      <FormattedMessage
                        id="MainPanelHeader.foundResults"
                        values={{ count: totalItems }}
                      />
                    )}
                  </div>
                  <div className={css.followSearchSec}>
                    <p>{intl.formatMessage({ id: 'SearchPage.followSearchSec.description' })}</p>
                  </div>

                  <div className={css.appliedFiltersSec}>
                    {selectedCategoryFilters.map(({ param, key }) => {
                      const label = this.getCategoryLabel(key, listingCategories);
                      return (
                        <div key={param} className={css.filter}>
                          <button onClick={() => this.removeFilter(param)}>
                            <Icons name="close" />
                          </button>
                        </div>
                      );
                    })}
                    <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                      <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                    </button>
                  </div>

                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                        isDesktop
                      />
                    );
                  })}
                </div>
              </aside>

              <div className={css.layoutWrapperMain} role="main">
                <div className={css.searchResultContainer}>
                  <SectionShopByCategory
                    intl={intl}
                    useHistoryPush={useHistoryPush}
                    listingCategories={listingCategories}
                  />
                  <SearchFiltersMobile
                    className={css.searchFiltersMobileList}
                    urlQueryParams={validQueryParams}
                    sortByComponent={sortBy('mobile')}
                    listingsAreLoaded={listingsAreLoaded}
                    resultsCount={totalItems}
                    searchInProgress={searchInProgress}
                    searchListingsError={searchListingsError}
                    showAsModalMaxWidth={MODAL_BREAKPOINT}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onOpenModal={this.onOpenMobileModal}
                    onCloseModal={this.onCloseMobileModal}
                    resetAll={this.resetAll}
                    selectedFiltersCount={selectedFiltersCountForMobile}
                    isMapVariant={false}
                    noResultsInfo={noResultsInfo}
                  >
                    {availableFilters.map(filterConfig => {
                      const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                        filterConfig.key
                      }`;

                      return (
                        <FilterComponent
                          key={key}
                          idPrefix="SearchFiltersMobile"
                          config={filterConfig}
                          listingCategories={listingCategories}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          liveEdit
                          showAsPopup={false}
                        />
                      );
                    })}
                  </SearchFiltersMobile>

                  <MainPanelHeader
                    className={css.mainPanel}
                    sortByComponent={sortBy('desktop')}
                    isSortByActive={sortConfig.active}
                    listingsAreLoaded={listingsAreLoaded}
                    resultsCount={totalItems}
                    searchInProgress={searchInProgress}
                    searchListingsError={searchListingsError}
                    noResultsInfo={noResultsInfo}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={search}
                  />
                  {noResultsInfo ? noResultsInfo : null}

                  <div
                    className={classNames(css.listingsForGridVariant, {
                      [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                    })}
                  >
                    {searchListingsError ? (
                      <H3 className={css.error}>
                        <FormattedMessage id="SearchPage.searchError" />
                      </H3>
                    ) : null}
                    {!isValidDatesFilter ? (
                      <H5>
                        <FormattedMessage id="SearchPage.invalidDatesFilter" />
                      </H5>
                    ) : null}
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={listings}
                      pagination={listingsAreLoaded ? pagination : null}
                      search={parse(location.search)}
                      isMapVariant={false}
                      currentUser={currentUser}
                      onToggleFavorites={createToggleFavoritesHandler}
                      pendingFavorites={pendingFavorites}
                    />
                  </div>
                  {/* <SectionStillOnHunt /> */}
                  {/* <SectionAdBanner /> */}
                  {/* <SectionAdBannerSecondary /> */}
                  {/* <SectionConclusion /> */}
                  <SectionCollections />
                </div>
              </div>
            </div>
          </div>
          {/* <div className={css.reviewSummary}>
            <ReviewRating className={css.rating} />
            <div className={css.summaryText}>
              <strong>4.41</strong> avg. from <strong>33168</strong>
              <span className={css.verifiedReviewsLogo}>
                <img
                  src="https://chairish-prod-static-images.freetls.fastly.net/verified_reviews/logo.svg"
                  loading="lazy"
                  alt="Verified Reviews"
                  sizes="120px"
                />
              </span>
            </div>
          </div> */}
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const { pendingFavorites } = state.pendingActions;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    pendingFavorites,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
  onAddPendingFavorite: listingId => dispatch(addPendingFavorite(listingId)),
  onRemovePendingFavorite: listingId => dispatch(removePendingFavorite(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
